import axios from "axios";
import Cookie from "js-cookie";
import { BASE_API_URL } from "../config";

export const baseUrl =
  BASE_API_URL || "http://localhost:3001/api";

export const api = axios.create({
  baseURL: baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue = [];
let refreshTimerId = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const refreshToken = async () => {
  try {
    const refreshTokenValue = Cookie.get("refreshToken");
    console.log("refreshTokenValue", refreshTokenValue);
    if (!refreshTokenValue) {
      throw new Error("No refresh token available");
    }

    const response = await axios.post(`${baseUrl}/auth/refresh`, {
      refreshToken: refreshTokenValue,
    });

    const { tokens } = response.data.data;

    // Update cookies with new tokens
    Cookie.set("token", tokens.accessToken);
    Cookie.set("refreshToken", tokens.refreshToken);
    
    return tokens.accessToken;
  } catch (error) {
    throw error;
  }
};

// Decode JWT payload (base64url)
const decodeJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

const clearRefreshTimer = () => {
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
};



const isTokenExpiringSoon = (token) => {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  const skew = 5; // seconds
  return payload.exp - nowSeconds <= skew;
};

api.interceptors.request.use(
  async (config) => {
    const currentToken = Cookie.get("token");
    if (!currentToken) {
      return config;
    }

    // If token is expiring/expired, refresh it before sending the request
    if (isTokenExpiringSoon(currentToken)) {
      if (isRefreshing) {
        // wait for ongoing refresh
        await new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
      } else {
        isRefreshing = true;
        try {
          const newToken = await refreshToken();
          // Set default header for subsequent requests
          api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
          processQueue(null, newToken);
        } catch (e) {
          processQueue(e, null);
          throw e;
        } finally {
          isRefreshing = false;
        }
      }
    }

    const tokenToUse = Cookie.get("token");
    if (tokenToUse) {
      config.headers["Authorization"] = `Bearer ${tokenToUse}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log("401 error");
      try {
        const newToken = await refreshToken();
        processQueue(null, newToken);
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
